import * as React from "react";
import {withRouter} from "react-router";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import * as _ from "lodash";

import {RouterProps} from "../../../../shared/ts/interfaces/router";
import {PaginationState} from "../../../../shared/ts/helpers/pagination";
import {AppError, RequestState} from "../../../../shared/ts/helpers/util";
import {FormActions} from "../../../../shared/ts/helpers/create_form_actions";
import {fetch, HocFetch} from "../../../../shared/ts/decorators/fetch";
import {HocPagination, pagination} from "../../../../shared/ts/decorators/pagination";
import {HocSort, sort} from "../../../../shared/ts/decorators/sort";
import {PAGE_PARAM, SORT_PARAM} from "../../../../shared/ts/constants/queryParams";
import {Store} from "../../project/reducer";
import {Dispatch} from "../../../../shared/ts/interfaces/dispatch";
import {Dictionary} from "../../../../shared/ts/interfaces/structure";
import {closePanel, openPanel, openTab} from "../../context_panel/actions/context_panel";
import ContentBody from "../../project/components/content/ContentBody";
import CenterBox from "../../shared/layout/CenterBox";
import Icon from "../../shared/icon/Icon";
import Loader from "../../shared/loader/Loader";
import Main from "../../shared/layout/Main";
import {Panel, PanelBody} from "../../shared/panel/Panel";
import {Pagination} from "../../shared/pagination/Pagination";
import Content from "../../project/components/content/Content";
import ContentHeader from "../../project/components/content/ContentHeader";
import ContentHeaderTitle from "../../project/components/content/ContentHeaderTitle";
import ContextPanel from "../../context_panel/components/ContextPanel";
import {NativeListContextPanelHeader} from "../../advertisments/components/context_panel/NativeListContextPanelHeader";
import ContextPanelBody from "../../context_panel/components/ContextPanelBody";
import {TabsContent} from "../../../../shared/ts/components/tabs/TabsContent";
import {campaignListContextPanelActions} from "../../context_panel/reducers/context_panel";
import {IReelsCreator, IReelsCreatorFilterForm} from "../reducers/reelsCreator";
import {invalidateReelsCreatorList, reelsCreatorFetch} from "../actions/reels_creator/fetch";
import {filterReelsCreatorListFormActions} from "../actions/reels_creator/filter_reel_creator_list";
import {reelsCreatorDelete} from "../actions/reels_creator/delete";
import {ReelsCreatorList} from "../components/reels_creator/ReelsCreatorList";
import {ReelsCreatorListFilterForm} from "../components/context_panel/reelsCreator/ReelsCreatorListFilterForm";

const defaultPage = 1;
const defaultSort = "id";

interface IStateProps {
    natives: IReelsCreator[];
    pagination: PaginationState;
    request: RequestState;
    errors: AppError | null;
    contextPanelOpened: boolean;
    openedTabIndex: number;
    listFilterForm: IReelsCreatorFilterForm;
}

interface IActionsProps {
    listFilterFormActions: FormActions;
    actions: {
        invalidateList: typeof invalidateReelsCreatorList;
        openPanel: typeof openPanel;
        closePanel: typeof closePanel;
        openTab: typeof openTab;
        reelsCreatorDelete: typeof reelsCreatorDelete;
    };
}

interface IOwnProps {
    hoc: HocFetch & HocPagination & HocSort;
    onItemClick: (id: number) => void;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {
    additionalQuery: Record<string, string>;
    addNewItemUrl: string;
}


@pagination(PAGE_PARAM)
@sort(SORT_PARAM)
@fetch((state: Store) => state.video.reelsCreator.fetch, fetchActions, validateQuery)
export class ReelsCreatorListViewC extends React.Component<IProps, {}> {

    /**
     * Lifecycle
     */
    public componentWillUnmount() {
        this.props.actions.invalidateList();
    }

    /**
     * Callback
     */
    private onFilterSubmit = (values: Dictionary<any>) => {
        this.props.hoc.fetch.reset(values);
    };

    private onFilterClear = () => {
        this.props.listFilterFormActions.clear();
        this.props.hoc.fetch.reset({});
    };

    private onSortChange = (column: string): void => {
        this.props.hoc.sort.toggle(column, this.props.hoc.pagination.get());
    };

    private onContextPanelTabOpen = (tabId: number): void => {
        this.props.actions.openTab(campaignListContextPanelActions.openTab, tabId);
        this.props.actions.openPanel(campaignListContextPanelActions.openPanel);
    };

    private onContextPanelToggle = (): void => {
        if (this.props.contextPanelOpened) {
            this.props.actions.closePanel(campaignListContextPanelActions.closePanel);
        } else {
            this.props.actions.openPanel(campaignListContextPanelActions.openPanel);
        }
    };

    /**
     * Render
     */
    private renderEmptyList(): JSX.Element {
        return (
            <ContentBody className="df fg-1">
                <CenterBox>
                    <Icon icon="empty-list" className="empty-list-icon" />
                    <div className="tac fs30">Nic tu nie ma, zmień parametry szukania</div>
                </CenterBox>
            </ContentBody>
        );
    }

    private renderLoader(): JSX.Element {
        return (
            <CenterBox className="vhc">
                <Loader size="lg" />
            </CenterBox>
        );
    }

    private renderError(): JSX.Element {
        return (
            <CenterBox>
                <Icon icon="empty-list" className="empty-list-icon" />
                <div className="error-message tac fs30">Popraw błędne parametry szukania</div>
            </CenterBox>
        );
    }

    private renderList(loading: boolean): JSX.Element {
        const {hoc, natives, pagination} = this.props;
        return (
            <ContentBody>
                <Panel color="default" className="psr mb-xl">
                    <PanelBody>
                        <ReelsCreatorList
                            listItems={natives}
                            onSort={this.onSortChange}
                            sortDirection={hoc.sort.getDirection()}
                            sortName={hoc.sort.getName()}
                            loading={loading}
                            onDeleteClick={this.props.actions.reelsCreatorDelete}
                            onItemClick={this.props.onItemClick}
                        />
                    </PanelBody>
                </Panel>

                <div className="pagination-holder tac pss b-0">
                    <Pagination
                        totalCount={pagination.total}
                        pageSize={pagination.pageSize}
                        activePage={hoc.pagination.currentPage()}
                        onPageChange={hoc.pagination.set}
                    />
                </div>
            </ContentBody>
        );
    }

    private renderContent(): JSX.Element {
        const {request, natives} = this.props;

        if (request === RequestState.Success && _.isEmpty(natives)) {
            return this.renderEmptyList();
        }

        if (request === RequestState.Success || (request === RequestState.Waiting && !_.isEmpty(natives))) {
            return this.renderList(request === RequestState.Waiting);
        }

        if (request === RequestState.Error) {
            return this.renderError();
        }

        return this.renderLoader();
    }

    public render(): JSX.Element {
        return (
            <Main>
                <Content>
                    <ContentHeader>
                        <ContentHeaderTitle>Lista autorów reels</ContentHeaderTitle>
                    </ContentHeader>
                    {this.renderContent()}
                </Content>

                <ContextPanel opened={this.props.contextPanelOpened}>
                    <NativeListContextPanelHeader
                        opened={this.props.contextPanelOpened}
                        openedTabIndex={this.props.openedTabIndex}
                        onTabOpen={this.onContextPanelTabOpen}
                        onPanelToggle={this.onContextPanelToggle}
                    />

                    <ContextPanelBody className="filters-holder">
                        <TabsContent openedTabIndex={this.props.openedTabIndex}>
                            <ReelsCreatorListFilterForm
                                onSubmit={this.onFilterSubmit}
                                errors={this.props.errors ? this.props.errors.fieldErrors : null}
                                values={this.props.listFilterForm}
                                updateFormAction={this.props.listFilterFormActions.update}
                                clearForm={this.onFilterClear}
                            />
                        </TabsContent>
                    </ContextPanelBody>
                </ContextPanel>
            </Main>
        );
    }
}

const ConnectedReelsCreatorListView = connect(mapStateToProps, mapActionsToProps)(ReelsCreatorListViewC);

export const ReelsCreatorListView = withRouter(
    (props: RouterProps) => (
        <ConnectedReelsCreatorListView
            additionalQuery={{}}
        />
    )
);

function mapStateToProps(store: Store): IStateProps {
    return {
        natives: store.video.reelsCreator.natives,
        pagination: store.video.reelsCreator.pagination,
        request: store.video.reelsCreator.fetch.requestState,
        errors: store.video.reelsCreator.fetch.errors,
        contextPanelOpened: store.video.reelsCreator.list.contextPanel.opened,
        openedTabIndex: store.video.reelsCreator.list.contextPanel.openedTabIndex,
        listFilterForm: store.video.reelsCreator.list.filterForm
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        listFilterFormActions: bindActionCreators(filterReelsCreatorListFormActions, dispatch),
        actions: bindActionCreators({
            invalidateList: invalidateReelsCreatorList,
            openPanel,
            closePanel,
            openTab,
            reelsCreatorDelete
        }, dispatch)
    };
}

function fetchActions(dispatch: Dispatch, query: Dictionary<string>): void {
    dispatch(reelsCreatorFetch(query));
}

function validateQuery(query: Dictionary<string>, props: IProps): Dictionary<string> {
    return {
        [PAGE_PARAM]: query[PAGE_PARAM] || defaultPage.toString(),
        [SORT_PARAM]: query[SORT_PARAM] || defaultSort,
        ...props.additionalQuery
    };
}
